import type { ContainerDetailInput, ContainerInput } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { addJobContainer, removeJobContainer, saveJobContainerDetails, updateJobContainer } from '../../../jobs.api';

/**
 * Add, edit, remove a job's containers, and save its shipping/release/empty-return details —
 * `onChanged` (the job page's own `reload`) is how the caller sees the result, same idiom as
 * `JobAttachments`. Add/edit/remove are row actions on a short list, so failures toast (CLAUDE.md
 * → Feedback), like `CustomerContacts`'; the details form is a page-level save, so its failure
 * stays inline, like `CustomerDetailsForm`'s.
 */
export function useJobContainers(jobId: string, onChanged: () => Promise<void>) {
  const [savingContainer, setSavingContainer] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const toast = useToast();

  async function rowAction(action: () => Promise<unknown>, successMessage: string, errorMessage: string): Promise<boolean> {
    try {
      await action();
      await onChanged();
      toast.success(successMessage);
      return true;
    } catch {
      toast.error(errorMessage);
      return false;
    }
  }

  async function add(input: ContainerInput): Promise<boolean> {
    setSavingContainer(true);
    try {
      return await rowAction(() => addJobContainer(jobId, input), `Container ${input.number} added`, `Could not add container ${input.number}`);
    } finally {
      setSavingContainer(false);
    }
  }

  async function update(containerId: string, input: ContainerInput): Promise<boolean> {
    setSavingContainer(true);
    try {
      return await rowAction(
        () => updateJobContainer(jobId, containerId, input),
        `Container ${input.number} updated`,
        `Could not update container ${input.number}`,
      );
    } finally {
      setSavingContainer(false);
    }
  }

  const remove = (containerId: string, number: string) =>
    rowAction(() => removeJobContainer(jobId, containerId), `Container ${number} removed`, `Could not remove container ${number}`);

  async function saveDetails(input: ContainerDetailInput): Promise<boolean> {
    setDetailsError(null);
    setSavingDetails(true);
    try {
      await saveJobContainerDetails(jobId, input);
      await onChanged();
      toast.success('Container details saved');
      return true;
    } catch {
      setDetailsError('Could not save — check the fields and try again.');
      return false;
    } finally {
      setSavingDetails(false);
    }
  }

  return { savingContainer, add, update, remove, detailsError, savingDetails, saveDetails };
}
